import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ImagePicker from '@/components/ImagePicker';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical, X, ChevronDown, ChevronUp } from 'lucide-react';
import { API_URL } from '@/config/api';

const TABS = [
  { id: 'general', label: '基本資訊' },
  { id: 'footer', label: 'Footer' },
  { id: 'homepage', label: '首頁內容' },
  { id: 'pages', label: '頁面標題' },
  { id: 'navbar', label: '導航選單' },
  { id: 'tracking', label: '追蹤代碼' },
  { id: 'seo', label: 'SEO' },
];

const safeParse = (str, fallback) => { try { return JSON.parse(str) || fallback; } catch { return fallback; } };

// ── Link list editor ──────────────────────────────────────────
const LinkListEditor = ({ links, onChange, importLabel, importItems }) => {
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [showImport, setShowImport] = useState(false);

  const add = () => {
    if (!newLabel.trim()) return;
    onChange([...links, { label: newLabel.trim(), url: newUrl.trim() || '/' }]);
    setNewLabel(''); setNewUrl('');
  };
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));
  const moveUp = (i) => { if (i === 0) return; const a = [...links]; [a[i-1], a[i]] = [a[i], a[i-1]]; onChange(a); };
  const moveDown = (i) => { if (i === links.length - 1) return; const a = [...links]; [a[i], a[i+1]] = [a[i+1], a[i]]; onChange(a); };
  const importItem = (item) => {
    if (!links.find(l => l.label === item.label && l.url === item.url)) onChange([...links, item]);
  };

  return (
    <div className="space-y-3">
      {links.length > 0 && (
        <div className="space-y-1.5">
          {links.map((link, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded-lg text-sm">
              <span className="flex-1 font-medium truncate">{link.label}</span>
              <span className="text-xs text-muted-foreground truncate max-w-[120px]">{link.url}</span>
              <div className="flex gap-0.5 flex-shrink-0">
                <button type="button" onClick={() => moveUp(i)} disabled={i === 0} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"><ChevronUp className="h-3 w-3" /></button>
                <button type="button" onClick={() => moveDown(i)} disabled={i === links.length - 1} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"><ChevronDown className="h-3 w-3" /></button>
                <button type="button" onClick={() => remove(i)} className="p-1 hover:bg-red-100 text-red-500 rounded"><X className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="連結名稱" className="h-8 text-sm flex-1"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
        <Input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="URL (如 /courses)" className="h-8 text-sm flex-1"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
        <Button type="button" size="sm" variant="outline" className="h-8 flex-shrink-0" onClick={add} disabled={!newLabel.trim()}>
          <Plus className="h-3 w-3 mr-1" />新增
        </Button>
      </div>
      {importItems?.length > 0 && (
        <div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setShowImport(v => !v)}
              className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              {showImport ? '▲' : '▼'} {importLabel}
            </button>
            <button type="button" onClick={() => importItems.forEach(importItem)}
              className="text-xs text-blue-600 hover:underline">
              全部加入
            </button>
          </div>
          {showImport && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {importItems.map((item, i) => (
                <button key={i} type="button" onClick={() => importItem(item)}
                  className="text-xs px-2.5 py-1 border rounded-full hover:bg-blue-50 hover:border-blue-400 transition-colors">
                  + {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [col2Links, setCol2Links] = useState([]);
  const [col3Links, setCol3Links] = useState([]);
  const [courseCategories, setCourseCategories] = useState([]);
  const [formData, setFormData] = useState({
    company_name: '',
    company_name_en: '',
    logo_url: '',
    logo_height: 40,
    popup_enabled: false,
    popup_title: '',
    popup_content: '',
    popup_image_url: '',
    popup_button_text: '',
    popup_button_url: '',
    popup_delay: 2,
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    facebook_url: '',
    instagram_url: '',
    about_text: '',
    footer_links_title: '快速連結',
    footer_courses_title: '課程類別',
    footer_contact_title: '聯絡資訊',
    footer_show_phone: true,
    footer_show_whatsapp: false,
    footer_show_email: true,
    footer_show_address: true,
    google_analytics_id: '',
    meta_pixel_id: '',
    seo_title: '',
    seo_description: '',
    favicon_url: '',
    hero_title: '',
    hero_subtitle: '',
    hero_cta_text: '',
    hero_image_url: '',
    features_title: '',
    features_subtitle: '',
    instructors_title: '',
    instructors_subtitle: '',
    testimonials_title: '',
    testimonials_subtitle: '',
    cta_title: '',
    cta_subtitle: '',
    contact_hero_title: '',
    contact_hero_subtitle: '',
    navbar_items: '[]',
  });
  const [navItems, setNavItems] = useState([]);

  useEffect(() => {
    fetchSettings();
    axios.get(`${API_URL}/categories?type=course_type`)
      .then(res => setCourseCategories(res.data || []))
      .catch(() => {});
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      const data = response.data;
      setFormData(data);
      setNavItems(safeParse(data.navbar_items, []));
      setCol2Links(safeParse(data.footer_col2_links, []));
      setCol3Links(safeParse(data.footer_col3_links, []));
    } catch (error) {
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        navbar_items: JSON.stringify(navItems),
        footer_col2_links: JSON.stringify(col2Links),
        footer_col3_links: JSON.stringify(col3Links),
      };
      await axios.put(`${API_URL}/settings`, submitData);
      toast.success('設定已儲存');
      fetchSettings();
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const addNavItem = () => {
    setNavItems([...navItems, { path: '/', label: '新頁面' }]);
  };

  const removeNavItem = (index) => {
    setNavItems(navItems.filter((_, i) => i !== index));
  };

  const updateNavItem = (index, field, value) => {
    const updated = [...navItems];
    updated[index] = { ...updated[index], [field]: value };
    setNavItems(updated);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">載入中...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">網站設定</h1>
          <p className="text-muted-foreground">管理網站內容、導航選單和基本資訊</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>公司資訊</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>公司名稱 (中文)</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>公司名稱 (英文)</Label>
                      <Input
                        value={formData.company_name_en}
                        onChange={(e) => setFormData({ ...formData, company_name_en: e.target.value })}
                      />
                    </div>
                  </div>
                  <ImagePicker
                    label="Logo"
                    value={formData.logo_url}
                    onChange={(url) => setFormData({ ...formData, logo_url: url })}
                  />
                  <div>
                    <Label>Logo 高度：{formData.logo_height}px</Label>
                    <input
                      type="range"
                      min={24}
                      max={200}
                      value={formData.logo_height}
                      onChange={(e) => setFormData({ ...formData, logo_height: Number(e.target.value) })}
                      className="w-full mt-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>細 (24px)</span><span>大 (200px)</span>
                    </div>
                  </div>
                  <div>
                    <Label>關於我們（簡介）</Label>
                    <Textarea
                      value={formData.about_text}
                      onChange={(e) => setFormData({ ...formData, about_text: e.target.value })}
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>聯絡資訊</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>電話</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>WhatsApp 號碼（含區號）</Label>
                      <Input
                        value={formData.whatsapp}
                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                        placeholder="85295858258"
                      />
                      <p className="text-xs text-muted-foreground mt-1">用於 wa.me 連結，不需加 +</p>
                    </div>
                    <div>
                      <Label>電郵</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>地址</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="留空則不顯示"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>社交媒體</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Facebook 網址</Label>
                      <Input
                        value={formData.facebook_url}
                        onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div>
                      <Label>Instagram 網址</Label>
                      <Input
                        value={formData.instagram_url}
                        onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Footer Tab */}
          {activeTab === 'footer' && (
            <>
              {/* Col 2 */}
              <Card>
                <CardHeader>
                  <CardTitle>欄 2 — 快速連結</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>欄標題</Label>
                    <Input value={formData.footer_links_title} onChange={(e) => setFormData({ ...formData, footer_links_title: e.target.value })} className="mt-1 max-w-xs" />
                  </div>
                  <div>
                    <Label className="mb-2 block">連結列表</Label>
                    <LinkListEditor
                      links={col2Links}
                      onChange={setCol2Links}
                      importLabel="從導航選單加入"
                      importItems={navItems.map(n => ({ label: n.label, url: n.path }))}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Col 3 */}
              <Card>
                <CardHeader>
                  <CardTitle>欄 3 — 課程類別</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>欄標題</Label>
                    <Input value={formData.footer_courses_title} onChange={(e) => setFormData({ ...formData, footer_courses_title: e.target.value })} className="mt-1 max-w-xs" />
                  </div>
                  <div>
                    <Label className="mb-2 block">連結列表</Label>
                    <LinkListEditor
                      links={col3Links}
                      onChange={setCol3Links}
                      importLabel="從課程類別加入（連結至 /courses）"
                      importItems={courseCategories.map(c => ({ label: c.name, url: '/courses' }))}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Col 4 */}
              <Card>
                <CardHeader>
                  <CardTitle>欄 4 — 聯絡資訊</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>欄標題</Label>
                    <Input value={formData.footer_contact_title} onChange={(e) => setFormData({ ...formData, footer_contact_title: e.target.value })} className="mt-1 max-w-xs" />
                  </div>
                  <div>
                    <Label className="mb-2 block">顯示哪些資料（資料在「基本資訊」填寫）</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { key: 'footer_show_phone', label: '電話', val: formData.phone },
                        { key: 'footer_show_whatsapp', label: 'WhatsApp', val: formData.whatsapp },
                        { key: 'footer_show_email', label: '電郵', val: formData.email },
                        { key: 'footer_show_address', label: '地址', val: formData.address },
                      ].map(({ key, label, val }) => (
                        <label key={key} className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                          <input type="checkbox" checked={!!formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })} className="mt-0.5 h-4 w-4" />
                          <div>
                            <p className="text-sm font-medium">{label}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[100px]">{val || '（未填寫）'}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Homepage Tab */}
          {activeTab === 'homepage' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Hero 區域（頂部橫幅）</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>主標題</Label>
                    <Textarea
                      value={formData.hero_title}
                      onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                      rows={2}
                      placeholder="學車選擇智駕駛&#10;一Take Pass不是夢"
                    />
                    <p className="text-xs text-muted-foreground mt-1">換行會自動分成兩行顯示</p>
                  </div>
                  <div>
                    <Label>副標題</Label>
                    <Input
                      value={formData.hero_subtitle}
                      onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>CTA 按鈕文字</Label>
                    <Input
                      value={formData.hero_cta_text}
                      onChange={(e) => setFormData({ ...formData, hero_cta_text: e.target.value })}
                    />
                  </div>
                  <ImagePicker
                    label="Hero 背景圖片"
                    value={formData.hero_image_url}
                    onChange={(url) => setFormData({ ...formData, hero_image_url: url })}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>各區域標題</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>優勢區域 — 標題</Label>
                      <Input
                        value={formData.features_title}
                        onChange={(e) => setFormData({ ...formData, features_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>優勢區域 — 副標題</Label>
                      <Input
                        value={formData.features_subtitle}
                        onChange={(e) => setFormData({ ...formData, features_subtitle: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>導師區域 — 標題</Label>
                      <Input
                        value={formData.instructors_title}
                        onChange={(e) => setFormData({ ...formData, instructors_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>導師區域 — 副標題</Label>
                      <Input
                        value={formData.instructors_subtitle}
                        onChange={(e) => setFormData({ ...formData, instructors_subtitle: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>學員分享 — 標題</Label>
                      <Input
                        value={formData.testimonials_title}
                        onChange={(e) => setFormData({ ...formData, testimonials_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>學員分享 — 副標題</Label>
                      <Input
                        value={formData.testimonials_subtitle}
                        onChange={(e) => setFormData({ ...formData, testimonials_subtitle: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>底部 CTA — 標題</Label>
                      <Input
                        value={formData.cta_title}
                        onChange={(e) => setFormData({ ...formData, cta_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>底部 CTA — 副標題</Label>
                      <Input
                        value={formData.cta_subtitle}
                        onChange={(e) => setFormData({ ...formData, cta_subtitle: e.target.value })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Pages Tab */}
          {activeTab === 'pages' && (
            <Card>
              <CardHeader>
                <CardTitle>聯絡頁面標題</CardTitle>
                <p className="text-sm text-muted-foreground">用於「聯絡我們」頁面頂部橫幅</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>主標題</Label>
                  <Input
                    value={formData.contact_hero_title}
                    onChange={(e) => setFormData({ ...formData, contact_hero_title: e.target.value })}
                    placeholder="例如：聯絡我們"
                  />
                </div>
                <div>
                  <Label>副標題</Label>
                  <Input
                    value={formData.contact_hero_subtitle}
                    onChange={(e) => setFormData({ ...formData, contact_hero_subtitle: e.target.value })}
                    placeholder="例如：有任何問題或需要查詢課程？歡迎隨時聯絡我們"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Navbar Tab */}
          {activeTab === 'navbar' && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>導航選單</CardTitle>
                  <Button type="button" size="sm" variant="outline" onClick={addNavItem}>
                    <Plus className="h-4 w-4 mr-1" /> 新增項目
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">拖放排序或編輯導航列項目</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {navItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                      <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">顯示名稱</Label>
                          <Input
                            value={item.label}
                            onChange={(e) => updateNavItem(index, 'label', e.target.value)}
                            placeholder="首頁"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">路徑</Label>
                          <Input
                            value={item.path}
                            onChange={(e) => updateNavItem(index, 'path', e.target.value)}
                            placeholder="/"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                        onClick={() => removeNavItem(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {navItems.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      尚未有導航項目，點擊「新增項目」開始
                    </p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  可用路徑：/ (首頁)、/about、/courses、/instructors、/blog、/exam-guide、/testimonials、/contact
                </p>
              </CardContent>
            </Card>
          )}

          {/* Tracking Tab */}
          {activeTab === 'tracking' && (
            <Card>
              <CardHeader>
                <CardTitle>追蹤代碼</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Google Analytics ID</Label>
                  <Input
                    value={formData.google_analytics_id}
                    onChange={(e) => setFormData({ ...formData, google_analytics_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                </div>
                <div>
                  <Label>Meta (Facebook) Pixel ID</Label>
                  <Input
                    value={formData.meta_pixel_id}
                    onChange={(e) => setFormData({ ...formData, meta_pixel_id: e.target.value })}
                    placeholder="123456789012345"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <Card>
              <CardHeader>
                <CardTitle>SEO 設定</CardTitle>
                <p className="text-sm text-muted-foreground">控制網站在 Google 搜尋結果顯示的標題、描述及圖示</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Live preview */}
                <div className="p-4 bg-white border rounded-lg shadow-sm space-y-1">
                  <p className="text-xs text-gray-500 mb-2">預覽效果</p>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    {formData.favicon_url && <img src={formData.favicon_url} alt="" className="w-4 h-4 rounded-full object-cover" />}
                    <span>wise-driving.com</span>
                  </div>
                  <p className="text-blue-700 font-medium text-base leading-tight">{formData.seo_title || '（未填寫標題）'}</p>
                  <p className="text-gray-600 text-sm leading-snug">{formData.seo_description || '（未填寫描述）'}</p>
                </div>

                <div>
                  <Label>Google 搜尋標題</Label>
                  <Input
                    value={formData.seo_title}
                    onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                    placeholder="學車 - 智駕駛 Wise Driving"
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{(formData.seo_title || '').length}/60 字符（建議 50–60 字以內）</p>
                </div>

                <div>
                  <Label>Google 搜尋描述</Label>
                  <Textarea
                    value={formData.seo_description}
                    onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                    placeholder="提供私家車及輕型貨車駕駛課程，一條龍服務代辦筆試、學牌、重考排期及安排師傅授課。"
                    rows={3}
                    maxLength={160}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{(formData.seo_description || '').length}/160 字符（建議 120–160 字以內）</p>
                </div>

                <div>
                  <ImagePicker
                    label="網站圖示 (Favicon) — Google 搜尋 URL 旁邊的小圖示"
                    value={formData.favicon_url}
                    onChange={(url) => setFormData({ ...formData, favicon_url: url })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">建議：512×512px 正方形，PNG 格式。Google 通常需要 1–3 天才更新顯示。</p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-4">
            <Button type="submit" disabled={saving} size="lg">
              {saving ? '儲存中...' : '儲存設定'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
